<template>
	<div class="o-carSingle__step1">
		<div class="o-carSingle__formStepContent">

			<div
				class="o-carSingle__formBlocks -isZakelijk"
			>
				<div class="o-carSingle__formBlock u-shadow">

					<div class="o-carSingle__formBlockHeader">
						<h4 v-if="headerCopy.title">{{ headerCopy.title }}</h4>
						<Usp v-if="headerCopy.usp" :label="headerCopy.usp" />
					</div>

					<div class="o-carSingle__formField u-topSpace">
						<div class="o-carSingle__infoLabel">
							<label for="aankoopbedrag">
								<span>Aanschafprijs</span>
							</label>
							<InfoPopup label="Aanschafprijs">
							</InfoPopup>
						</div>
						<input
							class="u-textInput"
							id="aankoopbedrag"
							type="number"
							inputmode="numeric"
							step="1"
							min="0"
							v-model="aankoopbedrag"
							@blur="handleAankoopbedragBlur"
						/>
					</div>

					<div class="o-carSingle__formField u-topSpace">
						<RadioSegment
							:key="'term-months-lease'"
							v-model="termMonths"
							name="term-months-lease"
							label="Looptijd in maanden"
							:options="termOptions"
						/>
					</div>

					<div class="o-carSingle__splitField u-topSpace">
						<div>
							<div class="o-carSingle__infoLabel">
								<label for="downpayment">
									<span>Aanbetaling</span>
									<span class="u-fs14 -splitLabel" v-if="maxDownpayment > 0"> (max {{ formatCurrency( maxDownpayment ) }})</span>
								</label>
								<InfoPopup label="Aanbetaling">
									<p>{{ infoCopy.aanbetaling }}</p>
								</InfoPopup>
							</div>
							<input
								class="u-textInput"
								id="downpayment"
								type="number"
								inputmode="numeric"
								step="1"
								min="0"
								:max="maxDownpayment"
								v-model="downpayment"
								@blur="handleDownpaymentBlur"
							/>
						</div>
						<div>
							<div class="o-carSingle__infoLabel">
								<label for="final-term">
									<span>Slottermijn</span>
									<span class="u-fs14 -splitLabel" v-if="maxSlottermijn > 0"> (max {{ formatCurrency( maxSlottermijn ) }})</span>
								</label>
								<InfoPopup label="Slottermijn">
									<p>{{ infoCopy.slottermijn }}</p>
								</InfoPopup>
							</div>
							<input
								class="u-textInput"
								id="final-term"
								type="number"
								inputmode="numeric"
								step="1"
								min="0"
								:max="maxSlottermijn"
								v-model="finalTerm"
								@blur="handleFinalTermBlur"
							/>
						</div>
					</div>

					<div class="o-carSingle__formField -row u-topSpace">
						<span class="o-carSingle__infoLabel">
							<span>Maandbedrag</span>
							<InfoPopup label="Maandbedrag">
								<p>{{ infoCopy.maandbedrag }}</p>
							</InfoPopup>
						</span>
						<div class="u-priceUpdating">
							<strong>{{ formattedMaandbedrag }}</strong>
							<span class="u-colorGray"> p/m</span>
						</div>
					</div>

				</div>
			</div>
		</div>
	</div>
</template>

<script setup>

import { ref, computed, watch } from 'vue';
import RadioSegment from '@components/organisms/car-single/form/components/RadioSegment.vue';
import InfoPopup from '@components/organisms/car-single/form/components/InfoPopup.vue';
import Usp from '@components/organisms/car-single/form/components/Usp.vue';
import { getInfoCopy } from '@components/organisms/car-single/form/content/info.js';
import { calculateMaandbedrag } from './utils/calculation.js';
import { getSlottermijnPercentage } from '@components/_shared/lease/slottermijn.js';

const props = defineProps( {
	copyConfig: {
		type: Object,
		default: () => ( {} ),
	},
} );

const defaultAankoopbedrag = () => {
	const cfg = props.copyConfig && typeof props.copyConfig === 'object' ? props.copyConfig : {};
	const val = cfg.default_aankoopbedrag;
	const num = Number( val );
	return ( val !== '' && val != null && ! isNaN( num ) && num >= 0 ) ? Math.round( num ) : 25000;
};

const aankoopbedrag = ref( defaultAankoopbedrag() );
const termMonths = ref( 72 );
const downpayment = ref( 0 );
const finalTerm = ref( 0 );

const termOptions = computed( () => [ 12, 24, 36, 48, 60, 72 ] );

const headerCopy = computed( () => {
	const uspKey = 'api_zakelijk_lease_usp';
	const titleKey = 'api_zakelijk_lease_titel';
	const cfg = props.copyConfig && typeof props.copyConfig === 'object' ? props.copyConfig : {};
	return {
		title: cfg[ titleKey ] || 'Lease',
		usp: cfg[ uspKey ] || '',
	};
} );

const infoCopy = computed( () => getInfoCopy( props.copyConfig ) );

const maxDownpayment = computed( () => {
	const bedrag = Number( aankoopbedrag.value ) || 0;
	return Math.max( 0, bedrag - 4000 );
} );

const resterendBedrag = computed( () => {
	const bedrag = Number( aankoopbedrag.value ) || 0;
	const aanb = Number( downpayment.value ) || 0;
	return Math.max( 0, bedrag - aanb );
} );

const maxSlottermijn = computed( () => {
	const looptijd = termMonths.value || 72;
	const percentage = getSlottermijnPercentage( looptijd, 0 );
	return Math.round( resterendBedrag.value * percentage );
} );

const maandbedrag = computed( () => {
	const bedrag = Number( aankoopbedrag.value ) || 0;
	if ( bedrag < 4000 ) {
		return 0;
	}
	return calculateMaandbedrag( {
		aankoopbedrag: bedrag,
		looptijd: termMonths.value,
		aanbetaling: Number( downpayment.value ) || 0,
		slottermijn: Number( finalTerm.value ) || 0,
		type: 0,
	} );
} );

const formattedMaandbedrag = computed( () => {
	return new Intl.NumberFormat( 'nl-NL', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	} ).format( maandbedrag.value );
} );

const formatCurrency = ( value ) => {
	if ( value === null || value === undefined ) {
		return '';
	}
	const numValue = Number( value );
	const isWholeNumber = Number.isInteger( numValue );
	return new Intl.NumberFormat( 'nl-NL', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: isWholeNumber ? 0 : 2,
		maximumFractionDigits: isWholeNumber ? 0 : 2,
	} ).format( numValue );
};

const clampToMax = ( current, max ) => {
	const num = Number( current ) || 0;
	const m = Number( max ) || 0;
	if ( num > m ) {
		return m;
	}
	return num;
};

const handleAankoopbedragBlur = () => {
	const val = aankoopbedrag.value;
	if ( val === null || val === '' || ( typeof val === 'string' && val.trim() === '' ) ) {
		aankoopbedrag.value = defaultAankoopbedrag();
	}
	const num = Number( aankoopbedrag.value ) || 0;
	if ( num < 0 ) {
		aankoopbedrag.value = 0;
	}
};

const handleDownpaymentBlur = () => {
	const val = downpayment.value;
	if ( val === null || val === '' || ( typeof val === 'string' && val.trim() === '' ) ) {
		downpayment.value = 0;
		return;
	}
	downpayment.value = clampToMax( downpayment.value, maxDownpayment.value );
};

const handleFinalTermBlur = () => {
	const val = finalTerm.value;
	if ( val === null || val === '' || ( typeof val === 'string' && val.trim() === '' ) ) {
		finalTerm.value = 0;
		return;
	}
	finalTerm.value = clampToMax( finalTerm.value, maxSlottermijn.value );
};

watch( aankoopbedrag, ( newVal ) => {
	if ( newVal === null || newVal === '' || ( typeof newVal === 'string' && newVal.trim() === '' ) ) {
		return;
	}
	const num = typeof newVal === 'string' ? parseFloat( newVal.trim() ) : Number( newVal );
	if ( ! isNaN( num ) && num < 0 ) {
		aankoopbedrag.value = 0;
	}
} );

watch( downpayment, ( newVal ) => {
	if ( newVal === null || newVal === '' || ( typeof newVal === 'string' && newVal.trim() === '' ) ) {
		return;
	}
	const num = typeof newVal === 'string' ? parseFloat( newVal.trim() ) : Number( newVal );
	if ( ! isNaN( num ) && num > maxDownpayment.value ) {
		downpayment.value = maxDownpayment.value;
	}
} );

watch( finalTerm, ( newVal ) => {
	if ( newVal === null || newVal === '' || ( typeof newVal === 'string' && newVal.trim() === '' ) ) {
		return;
	}
	const num = typeof newVal === 'string' ? parseFloat( newVal.trim() ) : Number( newVal );
	if ( ! isNaN( num ) && num > maxSlottermijn.value ) {
		finalTerm.value = maxSlottermijn.value;
	}
} );

watch( termMonths, ( newVal ) => {
	if ( finalTerm.value > maxSlottermijn.value ) {
		finalTerm.value = maxSlottermijn.value;
	}
} );

watch( [ termMonths, resterendBedrag ], () => {
	if ( finalTerm.value > maxSlottermijn.value ) {
		finalTerm.value = maxSlottermijn.value;
	}
}, { immediate: false } );

</script>
